/**
 * Pure derivations for the fleet panel. Kept out of the component so the
 * drift rule and the partial-snapshot tolerance are testable on their own.
 */

export function hasInventory(project) {
  return Boolean(project && project.inventorySnapshot);
}

/**
 * Drift compares the project's baseline (`versionId`, an ODD suggestion id)
 * with the snapshot's `majorityVersion` (a raw CMDB version string) as
 * strings. That holds only where a vendor's suggestion id IS the version
 * string — verified true for F5, the only vendor in this flow today. For a
 * vendor with opaque suggestion ids this would report drift on every
 * refresh, so revisit when the second vendor lands.
 */
export function fleetPanelData(project) {
  if (!hasInventory(project)) return null;
  const s = project.inventorySnapshot || {};
  const baseline = project.versionId || null;
  const majorityVersion = s.majorityVersion || null;
  const totalCis = s.totalCis || 0;
  const versions = Array.isArray(s.versions) ? s.versions : [];

  // The backend's `matchingCount` (service-common/lib/python/plan_inventory/core.py)
  // counts the fleet's MAJORITY version, not the project's baseline — correct
  // for its own create-time purpose (baseline === majority then) but wrong to
  // display once they diverge: it would report the count of the version the
  // panel isn't even talking about. Derive the baseline's own count from the
  // versions[] breakdown instead; a baseline absent from the breakdown is a
  // true 0, not missing data — itself a strong drift signal.
  const baselineEntry = baseline ? versions.find(v => v.version === baseline) : null;
  const matchingCount = baselineEntry ? baselineEntry.ciCount || 0 : 0;
  const inconsistentCount = Math.max(totalCis - matchingCount, 0);

  return {
    baseline,
    majorityVersion,
    totalCis,
    matchingCount,
    inconsistentCount,
    versions,
    fetchedAt: s.fetchedAt || null,
    hasDrift: Boolean(baseline && majorityVersion && majorityVersion !== baseline)
  };
}

export function formatFetchedAt(iso) {
  if (!iso) return { text: '—', title: '' };
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { text: '—', title: iso };
  return {
    text: d.toLocaleString(undefined, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }),
    title: iso
  };
}
